import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../lib/utils";

const badgeVariants = cva(
  "group/badge inline-flex h-5 w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-4xl border border-transparent px-2 py-0.5 text-xs font-medium whitespace-nowrap transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&>svg]:pointer-events-none [&>svg]:size-3!",
  {
    variants: {
      variant: {
        // ── shadcn base variants ──────────────────────────────────────────
        default: "bg-primary text-primary-foreground [a]:hover:bg-primary/80",
        secondary: "bg-secondary text-secondary-foreground [a]:hover:bg-secondary/80",
        destructive:
          "bg-destructive/10 text-destructive focus-visible:ring-destructive/20 dark:bg-destructive/20 dark:focus-visible:ring-destructive/40 [a]:hover:bg-destructive/20",
        outline: "border-border text-foreground [a]:hover:bg-muted [a]:hover:text-muted-foreground",
        ghost: "hover:bg-muted hover:text-muted-foreground dark:hover:bg-muted/50",
        link: "text-primary underline-offset-4 hover:underline",

        // ── Bowfast expedition status ─────────────────────────────────────
        // Draft — neutral, not yet committed
        draft:
          "bg-slate-100 text-slate-600 border-slate-300 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-600",
        // Planning — ocean blue tint
        planning:
          "bg-[#dbeeff] text-[#034f81] border-[#047cc7]/40 dark:bg-[#14233c] dark:text-[#2f97d5] dark:border-[#047cc7]/30",
        // Open — teal, accepting crew berths
        open: "bg-[#ccfaf0] text-[#0a6b5e] border-[#14b8a6]/50 dark:bg-[#064d44] dark:text-[#5ee3c0] dark:border-[#14b8a6]/30",
        // Active — abyss-500 solid, underway
        active: "bg-[#047cc7] text-white border-[#036ba8] dark:bg-[#0b89d2] dark:border-[#0284c7]",
        // Completed — slate, closed out
        completed:
          "bg-slate-100 text-slate-600 border-slate-300 dark:bg-slate-700 dark:text-slate-300 dark:border-slate-500",
        // Cancelled — coral red muted
        cancelled:
          "bg-[#fee2e2] text-[#b91c1c] border-[#ef4444]/40 dark:bg-[#7f1d1d] dark:text-[#fca5a5] dark:border-[#ef4444]/30",

        // ── Bowfast crew / cert status ────────────────────────────────────
        // Invited — signal amber, awaiting response
        invited:
          "bg-[#fef3c7] text-[#92400e] border-[#f59e0b]/50 dark:bg-[#451a03] dark:text-[#fcd34d] dark:border-[#f59e0b]/30",
        // Confirmed — teal green
        confirmed:
          "bg-[#ccfaf0] text-[#0a6b5e] border-[#14b8a6]/50 dark:bg-[#064d44] dark:text-[#5ee3c0] dark:border-[#14b8a6]/30",
        // Pending — slate muted
        pending:
          "bg-slate-100 text-slate-500 border-slate-300 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-600",
        // Overdue — coral solid
        overdue: "bg-[#ef4444] text-white border-[#dc2626] dark:bg-[#dc2626] dark:border-[#b91c1c]",

        // ── Charter type ──────────────────────────────────────────────────
        charter:
          "bg-[#dbeeff] text-[#034f81] border-[#047cc7]/40 dark:bg-[#0e1b32] dark:text-[#60b4e8] dark:border-[#047cc7]/40",

        // ── Rank insignia — always dark navy, theme-independent ───────────
        // Used on dark backgrounds that don't participate in the light/dark
        // theme switch (sidebar footer, dropdown identity headers on dark).
        rank: "bg-[#0d1b2a] text-white/85 border-white/15 font-bold",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

// Dot colours keyed by variant — used for dotted prop
const DOT_COLOR: Partial<Record<string, string>> = {
  draft: "bg-slate-400",
  planning: "bg-[#047cc7]",
  open: "bg-[#0a6b5e]",
  active: "bg-white",
  completed: "bg-slate-400",
  cancelled: "bg-[#b91c1c]",
  invited: "bg-[#92400e]",
  confirmed: "bg-[#0a6b5e]",
  pending: "bg-slate-400",
  overdue: "bg-white",
  charter: "bg-[#047cc7]",
  rank: "bg-white/60",
  default: "bg-primary-foreground",
  secondary: "bg-secondary-foreground",
  destructive: "bg-destructive",
  outline: "bg-foreground",
  ghost: "bg-foreground",
};

interface BadgeProps extends useRender.ComponentProps<"span">, VariantProps<typeof badgeVariants> {
  /** Show a status dot before the label */
  dotted?: boolean;
  /** Animate the dot with a CSS pulse (requires dotted) */
  pulse?: boolean;
}

function Badge({ className, variant = "default", dotted, pulse, render, children, ...props }: BadgeProps) {
  const dotColor = DOT_COLOR[variant ?? "default"] ?? "bg-foreground";

  return useRender({
    defaultTagName: "span",
    props: mergeProps<"span">(
      {
        className: cn(badgeVariants({ variant }), className),
        children: (
          <>
            {dotted && (
              <span className="relative flex size-1.5 shrink-0 items-center justify-center">
                {pulse && (
                  <span
                    className={cn("absolute inline-flex size-full animate-ping rounded-full opacity-75", dotColor)}
                  />
                )}
                <span className={cn("relative inline-flex size-1.5 rounded-full", dotColor)} />
              </span>
            )}
            {children}
          </>
        ),
      },
      props,
    ),
    render,
    state: {
      slot: "badge",
      variant,
    },
  });
}

export type { BadgeProps };
export { Badge, badgeVariants };
